import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  DEFAULT_LANGUAGE,
  MULTILANGUAGE_ENABLED,
  LANGUAGE_PREFERENCE_FIELD,
  LANGUAGE_PROFILE_SYNC_PENDING_KEY,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  findSupportedLanguage,
} from '../config/supportedLanguages';
import { updateLanguagePreference } from '../ducks/user.duck';

const LanguageContext = createContext(null);

const getStoredLanguage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return findSupportedLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch (e) {
    return null;
  }
};

const storeLanguage = language => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch (e) {
    // Language switching should still work if browser storage is unavailable.
  }
};

const isProfileSyncPending = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.localStorage.getItem(LANGUAGE_PROFILE_SYNC_PENDING_KEY) === 'true';
  } catch (e) {
    return false;
  }
};

const setProfileSyncPending = isPending => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (isPending) {
      window.localStorage.setItem(LANGUAGE_PROFILE_SYNC_PENDING_KEY, 'true');
    } else {
      window.localStorage.removeItem(LANGUAGE_PROFILE_SYNC_PENDING_KEY);
    }
  } catch (e) {
    // Profile synchronization can still be attempted if browser storage is unavailable.
  }
};

export const LanguageProvider = props => {
  const { children } = props;
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.user.currentUser);
  const updateInProgress = useSelector(state => state.user.languagePreferenceUpdateInProgress);
  const updateError = useSelector(state => state.user.languagePreferenceUpdateError);

  const [currentLanguage, setCurrentLanguage] = useState(DEFAULT_LANGUAGE);
  const pendingProfileLanguage = useRef(null);

  const profileLanguageValue =
    currentUser?.attributes?.profile?.publicData?.[LANGUAGE_PREFERENCE_FIELD];
  const profileLanguage = MULTILANGUAGE_ENABLED
    ? findSupportedLanguage(profileLanguageValue)
    : null;
  const currentUserId = currentUser?.id?.uuid;

  useEffect(() => {
    if (!MULTILANGUAGE_ENABLED) {
      pendingProfileLanguage.current = null;
      setCurrentLanguage(DEFAULT_LANGUAGE);
      storeLanguage(DEFAULT_LANGUAGE);
      setProfileSyncPending(false);
      return;
    }

    if (
      currentUserId &&
      pendingProfileLanguage.current &&
      profileLanguage?.code !== pendingProfileLanguage.current
    ) {
      return;
    }

    if (
      currentUserId &&
      pendingProfileLanguage.current &&
      profileLanguage?.code === pendingProfileLanguage.current
    ) {
      pendingProfileLanguage.current = null;
      setProfileSyncPending(false);
    }

    const storedLanguage = getStoredLanguage();
    const defaultLanguage = findSupportedLanguage(DEFAULT_LANGUAGE);

    if (!currentUserId) {
      const resolvedLanguage = storedLanguage || defaultLanguage;
      setCurrentLanguage(resolvedLanguage.code);
      storeLanguage(resolvedLanguage.code);
      return;
    }

    const shouldSyncGuestLanguage = isProfileSyncPending() && storedLanguage;
    if (shouldSyncGuestLanguage) {
      setCurrentLanguage(storedLanguage.code);
      storeLanguage(storedLanguage.code);

      if (profileLanguage?.code === storedLanguage.code) {
        setProfileSyncPending(false);
        return;
      }

      pendingProfileLanguage.current = storedLanguage.code;
      dispatch(updateLanguagePreference(storedLanguage.code))
        .then(() => {
          pendingProfileLanguage.current = null;
          setProfileSyncPending(false);
        })
        .catch(() => {
          pendingProfileLanguage.current = null;
        });
      return;
    }

    const resolvedLanguage = profileLanguage || storedLanguage || defaultLanguage;
    setCurrentLanguage(resolvedLanguage.code);
    storeLanguage(resolvedLanguage.code);

    if (!profileLanguage) {
      pendingProfileLanguage.current = resolvedLanguage.code;
      dispatch(updateLanguagePreference(resolvedLanguage.code))
        .then(() => {
          pendingProfileLanguage.current = null;
          setProfileSyncPending(false);
        })
        .catch(() => {
          pendingProfileLanguage.current = null;
        });
    }
  }, [currentUserId, dispatch, profileLanguage?.code]);

  useEffect(() => {
    if (!MULTILANGUAGE_ENABLED) {
      return undefined;
    }

    const handleStorageChange = event => {
      if (event.key !== LANGUAGE_STORAGE_KEY || currentUserId) {
        return;
      }

      const storedLanguage = findSupportedLanguage(event.newValue);
      setCurrentLanguage(storedLanguage?.code || DEFAULT_LANGUAGE);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('storage', handleStorageChange);
      return () => window.removeEventListener('storage', handleStorageChange);
    }

    return undefined;
  }, [currentUserId]);

  const changeLanguage = useCallback(
    languageValue => {
      if (!MULTILANGUAGE_ENABLED) {
        setCurrentLanguage(DEFAULT_LANGUAGE);
        return Promise.resolve();
      }

      const language = findSupportedLanguage(languageValue);
      if (!language) {
        return Promise.resolve();
      }

      setCurrentLanguage(language.code);
      storeLanguage(language.code);

      if (!currentUserId) {
        setProfileSyncPending(true);
        return Promise.resolve();
      }

      if (profileLanguage?.code === language.code) {
        setProfileSyncPending(false);
        return Promise.resolve();
      }

      setProfileSyncPending(true);
      pendingProfileLanguage.current = language.code;
      return dispatch(updateLanguagePreference(language.code))
        .then(() => {
          pendingProfileLanguage.current = null;
          setProfileSyncPending(false);
        })
        .catch(error => {
          pendingProfileLanguage.current = null;
          throw error;
        });
    },
    [currentUserId, dispatch, profileLanguage?.code]
  );

  const language =
    findSupportedLanguage(currentLanguage) || findSupportedLanguage(DEFAULT_LANGUAGE);

  const value = useMemo(
    () => ({
      currentLanguage: language.code,
      language,
      supportedLanguages: SUPPORTED_LANGUAGES,
      multilanguageEnabled: MULTILANGUAGE_ENABLED,
      changeLanguage,
      updateInProgress,
      updateError,
    }),
    [changeLanguage, language, updateError, updateInProgress]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }

  return context;
};
